import React, { useRef, useMemo, useEffect } from 'react'
import * as yup from 'yup'

import { Form, Button, TextInput, FormButtons, DatePicker } from '@/components'
import CustomerSelector from '@/pages/Customer/customerSelector'
import { IDriver } from '@/types'
import { useForm } from '@/hooks'
import { print } from '@/utils'
import { t } from '@/i18n'

import { buildData } from '../utils'

interface Props {
  loading: boolean
  isEditing?: boolean
  submitting: boolean
  onCancel: () => void
  initialData?: Partial<IDriver>
  onSubmit: (d: IDriver) => void
}

const DriverForm: React.FC<Props> = ({
  loading,
  onCancel,
  onSubmit,
  isEditing,
  submitting,
  initialData,
}) => {
  const schema = useMemo(
    () =>
      yup
        .object({
          name: yup.string().trim().required(t('required_field_error')),
          customer: yup.string().required(t('required_field_error')),
          email: yup
            .string()
            .trim()
            .email()
            .required(t('invalid_format_error')),
        })
        .required(),
    [],
  )

  const { control, isValid, getValues, onTriggerChange, onFormSubmit } =
    useForm<any>({
      isEditing,
      defaultValues: initialData,
      schema,
      onSubmit,
    })

  const formRef = useRef<HTMLFormElement | null>(null)

  const emptyValue = useMemo(() => (isEditing ? '' : '...'), [isEditing])

  useEffect(() => {
    if (initialData?.customer) {
      onTriggerChange('customer', initialData.customer.id)
    }
  }, [initialData?.customer, onTriggerChange])

  const driverFields = useMemo(() => buildData(initialData), [initialData])

  return (
    <>
      <div className="main-info">
        <div className="close-box">
          <Button onClick={onCancel}>
            <span className="icon-close"></span>
          </Button>
        </div>
        <div className="section-box">
          <div className="toolbox">
            <div className="title-box">
              <h4>{t('edit_driver')}</h4>
            </div>
            <div className="btn d-flex">
              <Button
                className="main-btn light"
                onClick={() => print('report', initialData?.id)}
              >
                <span className="icon-print"></span>
                {t('print')}
              </Button>
            </div>
          </div>

          <div className="edit-data">
            <Form<IDriver>
              formRef={formRef}
              control={control}
              isEditing={isEditing}
              onSubmit={onFormSubmit}
            >
              <div className="form-fields">
                {driverFields.map(field => {
                  if (!field.name) return null
                  return (
                    <TextInput
                      key={field.name}
                      name={field.name}
                      control={control}
                      label={t(field.label)}
                      emptyValue={emptyValue}
                      className={field.className}
                      disabled={!Boolean(isEditing)}
                    />
                  )
                })}
                <DatePicker
                  name="birthday"
                  className="w-50"
                  control={control}
                  label={t('birthday')}
                  emptyValue={emptyValue}
                  dateFormat="yyyy-MM-dd"
                  disabled={!Boolean(isEditing)}
                />
                <CustomerSelector
                  isFormElement
                  className="w-50"
                  control={control}
                  isEditing={isEditing}
                  initial={initialData?.customer}
                  selected={getValues('customer')}
                />
              </div>
            </Form>
          </div>
        </div>
      </div>
      <FormButtons
        formRef={formRef}
        visible={isEditing}
        onCancel={onCancel}
        isFormValid={isValid}
        submitting={submitting}
        cancelClassName={'gray'}
      />
    </>
  )
}

export default DriverForm
